import { useMutation, useQuery } from '@tanstack/react-query';

import { SHARED_CONSTANTS } from '../../Shared/constants/commonConstants';
import { ApiClient } from '../../Shared/interfaces/ApiClient';
import { ApiError } from '../../Shared/interfaces/ApiError';
import {
  BRAND_CONFIRMATION_VALUE,
  getApiValue,
  RO_IN_VALUE
} from '../components/CreateBooking/config/dropDownConfig';
import {
  BOOKING_STATUS
} from '../components/CreateBooking/constants/DetailedBookingConstants';
import {
  BookingDetails, BookingFormData, CreateBookingResponse, EditableFormData, EditBookingPayload
} from '../components/CreateBooking/interface/BookingFormInterface';
import {
  SlotPackagesResponse
} from '../components/CreateBooking/interface/DetailedBookingInterface';
import {
  BookingSheetApiParams, BookingSummaryItem, ContextualNodeDetails, QueueSheetManagementRows,
} from '../components/QueueSheetManagement/interface/QueueSheetInterface';
import { EndpointQueryKeys, ENDPOINTS } from '../constants/endpoints';
import { FIELD_MAPPING } from '../constants/formConstants';
import {
  createBookingApi, editBookingDetails, getBookingDetailsById, getSlotPackages
} from './service';

const { GET_BOOKING_SHEET } = ENDPOINTS;
const { GET_BOOKING_SHEET: BOOKING_SHEET_QUERY_KEY, GET_BOOKING_DETAILS, GET_SLOT_PACKAGES } = EndpointQueryKeys

/**
 * Derives slot display string from contextualNodes.
 * Groups nodes by slotOrPackageName and lists their segment names.
 */
export const formatSlotNamesFromNodes = (nodes: ContextualNodeDetails[] = []): string => {
  if (!nodes.length) return '-';
  const grouped = new Map<string, string[]>();
  for (const node of nodes) {
    const pkg = node.slotOrPackageName || '';
    if (!pkg) continue;
    if (!grouped.has(pkg)) grouped.set(pkg, []);
    if (node.contextualNode) grouped.get(pkg)!.push(node.contextualNode);
  }
  return Array.from(grouped.entries())
    .map(([pkg, segments]) =>
      segments.length ? `${pkg} [${segments.join(', ')}]` : pkg
    ).join('\n') || '-';
};

/**
 * Extracts unique slot package names from contextualNodes.
 */
export const getSlotPackageNamesFromNodes = (nodes: ContextualNodeDetails[] = []): string[] => {
  return Array.from(new Set(nodes.map(n => n.slotOrPackageName).filter(Boolean)));
};

export const transformBookingItem = (
  item: BookingSummaryItem
): QueueSheetManagementRows => {
  const nodes = item.contextualNodes || [];
  return {
    bookingId: item.bookingId || "",
    entryId: item.entryId || 0,
    brand: item.brandName,
    aeName: item.accountExecName,
    amName: item.accountManagerName,
    category: item.categoryName,
    typeOfPurchase: item.typeOfPurchase,
    exactBillingValue: item.roValue,
    sfOpportunityId: item.sfOpportunityId,
    ro: item.roIn ? RO_IN_VALUE.YES : RO_IN_VALUE.NO,
    brandConfirmation: item.brandConfirmation ? BRAND_CONFIRMATION_VALUE.YES : BRAND_CONFIRMATION_VALUE.NO,
    rodeoAdName: item.rodeoAdvertiserName,
    gtmStatus: item.gtmStatus,
    commentsByPoc: item.commentsByPOC || '',
    creatives: item.creativesStage,
    modifiedAt: item.modifiedAt,
    modifiedBy: item.modifiedBy,
    slotNames: formatSlotNamesFromNodes(nodes),
    slotPackageNames: getSlotPackageNamesFromNodes(nodes),
    contextualNodes: nodes,
  };
};

const fetchBookingSheet = async (
  api: ApiClient,
  params: BookingSheetApiParams
): Promise<QueueSheetManagementRows[]> => {
  const { sheetName: artEventId, bookingStatus = BOOKING_STATUS.ACTIVE } = params;
  const urlParams = new URLSearchParams({
    artEventId,
    bookingStatus
  });

  const endpoint = `${GET_BOOKING_SHEET}?${urlParams.toString()}`;

  return api
    .get(endpoint)
    .then(
      (apiResponse) => {
        const bookings: QueueSheetManagementRows[] = apiResponse.bookingSummary?.map(transformBookingItem) || [];
        return bookings.sort((a, b) => b.entryId - a.entryId);
      }
    );
};

export const useBookingSheet = (
  api: ApiClient,
  params: BookingSheetApiParams
) => {
  return useQuery({
    queryKey: [BOOKING_SHEET_QUERY_KEY, params.sheetName, params.bookingStatus],
    queryFn: () => fetchBookingSheet(api, params),
    enabled: !!params.sheetName && params.sheetName.trim() !== '',
    staleTime: SHARED_CONSTANTS.STALE_TIME_FOR_REACT_QUERY,
  });
};

export const useCreateBooking = (api: ApiClient, artEventId: string) =>
  useMutation<CreateBookingResponse, ApiError, BookingFormData>({
    mutationFn: (formData: BookingFormData) =>
      createBooking(api, formData, artEventId),
  });

export const useBookingDetails = (api: ApiClient, bookingId: string, enabled: boolean = true) => useQuery<BookingDetails, ApiError>({
  queryKey: [GET_BOOKING_DETAILS, bookingId],
  queryFn: () => getBookingDetailsById(api, bookingId),
  enabled: enabled && !!bookingId,
});

export const createBooking = async (
  api: ApiClient,
  formData: BookingFormData,
  artEventId: string
): Promise<CreateBookingResponse> => {
  let payload: BookingDetails['booking'] = {
    artEventId,
    accountExecName: formData.aeName,
    accountManagerName: formData.amName,
    categoryName: formData.category,
    orgName: formData.org,
    rodeoAdvertiserName: formData.rodeoAdName,
    brandName: formData.brand,
    roValue: formData.exactBillingValue,
    sfOpportunityId: formData.sfOpportunityId,
    typeOfPurchase: formData.typeOfPurchase,

    bookingStatus: BOOKING_STATUS.ACTIVE,
  };

  if (formData.creatives) {
    const apiCreativesValue = getApiValue(formData.creatives);
    if (apiCreativesValue)
      payload = {
        ...payload,
        creativesStage: apiCreativesValue,
      };
  }

  if (formData.ro)
    payload = { ...payload, roIn: formData.ro === RO_IN_VALUE.YES };

  if (formData.brandConfirmation)
    payload = { ...payload, brandConfirmation: formData.brandConfirmation === BRAND_CONFIRMATION_VALUE.YES };

  if (formData.commentsByPoc)
    payload = {
      ...payload,
      commentsByPOC: formData.commentsByPoc,
    };

  return createBookingApi(api, payload);
};

export const editBooking = async (
  api: ApiClient,
  bookingId: string,
  editableFormData: EditableFormData
) => {
  const { contextualNodes, ...restEditableFormData } = editableFormData;

  const payload: EditBookingPayload['booking'] = {};

  if (contextualNodes) payload.contextualNodes = contextualNodes;

  Object.entries(FIELD_MAPPING).forEach(([formField, apiField]) => {
    const value = restEditableFormData[formField as keyof typeof restEditableFormData];
    if (value !== undefined && value !== null) {
      (payload as any)[apiField] = value;
    }
  });

  if (restEditableFormData.ro !== undefined)
    payload.roIn = restEditableFormData.ro === RO_IN_VALUE.YES;

  if (restEditableFormData.brandConfirmation !== undefined)
    payload.brandConfirmation = restEditableFormData.brandConfirmation === BRAND_CONFIRMATION_VALUE.YES;

  if (restEditableFormData.creatives) {
    const apiCreativesValue = getApiValue(restEditableFormData.creatives);
    if (apiCreativesValue) payload.creativesStage = apiCreativesValue;
  }

  return editBookingDetails(api, bookingId, payload);
};

export const getSlotPackagesData = (api: ApiClient, typeOfPurchase: string, artEventId: string) => {
  return useQuery<SlotPackagesResponse, ApiError>({
    queryKey: [GET_SLOT_PACKAGES, typeOfPurchase],
    queryFn: () => getSlotPackages(api, typeOfPurchase, artEventId),
    enabled: !!typeOfPurchase,
  });
};

